import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  createAdditionalCompensation,
  updateAdditionalCompensation,
  type AdditionalCompensation,
  type CreateAdditionalCompensationInput,
  type UpdateAdditionalCompensationInput,
} from '@/lib/employees';
import { parseDateOnly, toDateParam } from '@/lib/date';
import { Loader2 } from 'lucide-react';

interface AdditionalCompensationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  entry?: AdditionalCompensation | null;
}

const CURRENCIES = ['USD', 'EUR', 'GBP', 'UAH', 'PLN', 'CZK', 'CHF', 'CAD', 'AUD'];

// Free-text bucket; same set PeopleForce uses by default. User can type a
// custom value too — we accept any string under 60 chars.
const COMPENSATION_TYPES = ['Bonus', 'Equity', 'Stipend', 'Allowance', 'Commission', 'Other'];

const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'yearly', label: 'Yearly' },
];

export function AdditionalCompensationDialog({
  open,
  onOpenChange,
  employeeId,
  entry,
}: AdditionalCompensationDialogProps) {
  const queryClient = useQueryClient();
  const isEditing = !!entry;

  const [type, setType] = useState<'reoccurring' | 'fixed'>('reoccurring');
  const [compensationType, setCompensationType] = useState('Bonus');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [startsOn, setStartsOn] = useState<Date | null>(null);
  const [endsOn, setEndsOn] = useState<Date | null>(null);
  const [frequency, setFrequency] = useState('monthly');

  useEffect(() => {
    if (!open) return;
    if (entry) {
      setType(entry.type);
      setCompensationType(entry.compensationType || 'Bonus');
      setDescription(entry.description ?? '');
      setAmount(String(parseFloat(entry.amount)));
      setCurrency(entry.currency);
      setStartsOn(entry.startsOn ? parseDateOnly(entry.startsOn) : null);
      setEndsOn(entry.endsOn ? parseDateOnly(entry.endsOn) : null);
      setFrequency(entry.frequency || 'monthly');
    } else {
      setType('reoccurring');
      setCompensationType('Bonus');
      setDescription('');
      setAmount('');
      setCurrency('USD');
      setStartsOn(null);
      setEndsOn(null);
      setFrequency('monthly');
    }
  }, [open, entry]);

  const createMutation = useMutation({
    mutationFn: (input: CreateAdditionalCompensationInput) =>
      createAdditionalCompensation(employeeId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['employee', employeeId, 'additional-compensations'],
      });
      onOpenChange(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: UpdateAdditionalCompensationInput) =>
      updateAdditionalCompensation(employeeId, entry!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['employee', employeeId, 'additional-compensations'],
      });
      onOpenChange(false);
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount < 0) return;

    const startsOnStr = toDateParam(startsOn);
    if (!startsOnStr) return;

    const isReoccurring = type === 'reoccurring';

    const payload = {
      type,
      compensationType: compensationType || undefined,
      description: description || undefined,
      amount: parsedAmount,
      currency,
      startsOn: startsOnStr,
      endsOn: isReoccurring ? (toDateParam(endsOn) ?? undefined) : undefined,
      frequency: isReoccurring ? frequency : undefined,
    };

    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Bonus' : 'Add Bonus'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update this bonus or stipend entry.'
              : 'Record a recurring stipend or a one-time payment.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Type — reoccurring vs fixed */}
            <div className="space-y-2">
              <Label>Type</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setType('reoccurring')}
                  disabled={isPending}
                  className={`rounded-md border px-3 py-2 text-left text-sm transition ${
                    type === 'reoccurring'
                      ? 'border-hygge bg-hygge/10 text-foreground'
                      : 'border-border bg-background text-muted-foreground hover:bg-muted/40'
                  }`}
                >
                  <div className="font-medium">Reoccurring</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Monthly / weekly / yearly
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setType('fixed')}
                  disabled={isPending}
                  className={`rounded-md border px-3 py-2 text-left text-sm transition ${
                    type === 'fixed'
                      ? 'border-hygge bg-hygge/10 text-foreground'
                      : 'border-border bg-background text-muted-foreground hover:bg-muted/40'
                  }`}
                >
                  <div className="font-medium">One-time</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Single lump-sum payment
                  </div>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="500"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={isPending}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select value={currency} onValueChange={setCurrency} disabled={isPending}>
                  <SelectTrigger id="currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="compensationType">Category</Label>
                <Select
                  value={compensationType}
                  onValueChange={setCompensationType}
                  disabled={isPending}
                >
                  <SelectTrigger id="compensationType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPENSATION_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {type === 'reoccurring' && (
                <div className="space-y-2">
                  <Label htmlFor="frequency">Frequency</Label>
                  <Select value={frequency} onValueChange={setFrequency} disabled={isPending}>
                    <SelectTrigger id="frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQUENCIES.map((f) => (
                        <SelectItem key={f.value} value={f.value}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startsOn">
                  {type === 'fixed' ? 'Payment date' : 'Starts on'}
                </Label>
                <DatePicker
                  id="startsOn"
                  value={startsOn}
                  onChange={setStartsOn}
                  placeholder="Select date"
                  disabled={isPending}
                />
              </div>
              {type === 'reoccurring' && (
                <div className="space-y-2">
                  <Label htmlFor="endsOn">
                    Ends on <span className="text-muted-foreground text-[11px] font-normal">(optional)</span>
                  </Label>
                  <DatePicker
                    id="endsOn"
                    value={endsOn}
                    onChange={setEndsOn}
                    placeholder="Ongoing"
                    disabled={isPending}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="e.g. Performance Bonus Q2 2026"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isPending}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !startsOn || !amount}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Save changes' : 'Add bonus'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
